/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from "react"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { TextField, makeStyles } from "@material-ui/core"

const useStyles = makeStyles(() => ({
  select: {
    "& .MuiOutlinedInput-root": {
      fontSize: "14px",
      padding: "0",
      "& fieldset": {
        borderColor: "#D1D5DB"
      },
      "&:hover fieldset": {
        borderColor: "#1EAAC4"
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1EAAC4",
        borderWidth: "1px"
      }
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px !important"
    }
  },
  noFill: {
    color: "red"
  }
}))
const NCAutocomplete = props => {
  const isMultiple = props.multiple || false
  const [selectedOption, setSelectedOption] = useState(isMultiple ? [] : null)

  // Pull out props that belong to the Autocomplete / form value, NOT the raw
  // TextField. Spreading the parent's `value` (e.g. Formik's array of selected
  // ids) onto the inner input breaks typing + filtering — the search box
  // becomes controlled with a non-string value and never filters options.
  // `onBlur` is intentionally left in `rest` so Formik touched-tracking still works.
  // `clearable` opts a single-select into showing the clear (x) button and
  // treating an erased input as "no selection".
  const { value, onChange, onSearch, filterOptions, clearable, ...rest } = props

  // Keep selectedOption in sync when value or options change externally.
  // Only update when the resolved option ids actually change — producing a new
  // array reference on every options change makes MUI Autocomplete reset its
  // inputValue, which clears the typed text during async (server-side) search.
  useEffect(() => {
    if (isMultiple) {
      const ids = value != null && Array.isArray(value) ? value : []
      setSelectedOption(prev => {
        const prevArr = Array.isArray(prev) ? prev : []
        // Resolve each selected id against the current options, falling back to
        // the previously-resolved object when a server-side search shrinks the
        // options list (so already-selected chips are never dropped).
        const next = ids
          .map(id => (props.options || []).find(item => item.id === id) || prevArr.find(o => o.id === id))
          .filter(Boolean)
        const prevSig = prevArr.map(o => o.id).sort().join(",")
        const nextSig = next.map(o => o.id).sort().join(",")
        return prevSig === nextSig ? prev : next
      })
    } else {
      if (value != null) {
        setSelectedOption(prev => {
          // Fall back to the previously-resolved option when a server-side
          // search (or its loading state) drops it from the options list —
          // otherwise the selection resets to null and MUI wipes the typed text.
          const next =
            (props.options || []).find(item => item.id === value) ||
            (prev && prev.id === value ? prev : null)
          const prevSig = prev ? String(prev.id) : ""
          const nextSig = next ? String(next.id) : ""
          return prevSig === nextSig ? prev : next
        })
      } else {
        setSelectedOption(prev => (prev == null ? prev : null))
      }
    }
  }, [value, props.options])

  const cls = useStyles()
  return (
    <Autocomplete
      {...rest}
      id="combo-box-demo"
      options={props.options || []}
      disableClearable={!isMultiple && !clearable}
      getOptionLabel={option => option?.name || ""}
      filterOptions={filterOptions || (onSearch ? options => options : undefined)}
      getOptionSelected={props.getOptionSelected || ((option, value) => option.id === value.id)}
      value={selectedOption}
      onChange={(e, newValue) => {
        setSelectedOption(newValue)
        if (isMultiple) {
          onChange(newValue ? newValue.map(v => v.id) : [])
        } else {
          onChange(newValue?.id)
        }
      }}
      onInputChange={(e, inputValue, reason) => {
        // Erasing the text of a clearable single-select clears the selection;
        // otherwise MUI restores the previous option's label on blur.
        if (clearable && !isMultiple && reason === "input" && inputValue === "" && selectedOption) {
          setSelectedOption(null)
          onChange(undefined)
        }
      }}
      renderInput={params => (
        <TextField
          {...rest}
          {...params}
          className={cls.select}
          onChange={e => {
            if (onSearch) {
              onSearch(e.target.value)
            }
          }}
          variant="outlined"
        />
      )}
    />
  )
}
export default NCAutocomplete
